import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, mediaUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { Slider } from "./ui/slider";
import { useT } from "../lib/i18n";
import { useLiveStats } from "../context/LiveStats";
import { LiveNumber } from "./LiveNumber";
import { FxModeBar } from "./FxModeBar";
import { MixFxRow } from "./MixFxRow";
import { loadBuffer, subscribe } from "../lib/audioEngine";

export const useEngineActive = () => {
  const [active, setActive] = useState(new Set());
  useEffect(() => subscribe(setActive), []);
  return active;
};

const holdHandlers = (down, up) => ({
  onPointerDown: (e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); down(); },
  onPointerUp: up,
  onPointerCancel: up,
  onContextMenu: (e) => e.preventDefault(),
});

const Pad = ({ fx, idx, on, vol, setVol, hits, compact }) => {
  const [hit, setHit] = useState(0);
  const { fxPress, fxRelease } = usePlayer();
  const { t } = useT();
  const down = () => { setHit((h) => h + 1); fxPress(fx); };
  return (
    <div className="flex flex-col gap-3">
      <button
        data-testid={`fx-pad-${idx + 1}`}
        data-active={on ? "true" : "false"}
        {...holdHandlers(down, () => fxRelease(fx))}
        className={`relative aspect-square border overflow-hidden text-left p-3 select-none touch-none transition-[border-color,transform] duration-75 active:scale-[0.96] ${on ? "border-[#1FD1FF]" : "border-white/10 hover:border-white/40"} bg-[#0A1424]`}
        style={{ boxShadow: on ? `inset 0 0 40px ${fx.color}, 0 0 18px ${fx.color}` : `inset 0 -3px 0 ${fx.color}` }}
      >
        <motion.span key={hit} initial={{ opacity: hit ? 0.9 : 0, scale: 0.4 }} animate={{ opacity: 0, scale: 1.6 }} transition={{ duration: 0.45 }}
          className="absolute inset-0 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${fx.color}, transparent 70%)` }} />
        <span className="relative flex flex-wrap justify-between gap-x-2 font-mono text-[11px] text-[#A8B3C4]">
          <span>{idx < 9 ? `${t("fx.key")} ${idx + 1}` : ""}</span>
          <span data-testid={`fx-pad-hits-${idx + 1}`}><LiveNumber value={hits} /> {t("fx.uses")}</span>
        </span>
        <span className={`absolute bottom-2 left-2 right-2 font-display leading-none line-clamp-2 ${compact ? "text-base sm:text-2xl" : "text-2xl sm:text-3xl"}`}>{fx.name}</span>
      </button>
      <Slider data-testid={`fx-pad-volume-${idx + 1}`} value={[Math.round(vol * 100)]} max={100} step={1} onValueChange={(v) => setVol(v[0] / 100)} aria-label={`${fx.name} volume`} />
    </div>
  );
};

const useKeys = (fx, enabled) => {
  const { fxPress, fxRelease } = usePlayer();
  useEffect(() => {
    if (!enabled) return undefined;
    const pick = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return null;
      const n = parseInt(e.key, 10);
      return n >= 1 && n <= fx.length ? fx[n - 1] : null;
    };
    const down = (e) => { const f = pick(e); if (f && !e.repeat) fxPress(f); };
    const up = (e) => { const f = pick(e); if (f) fxRelease(f); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [fx, enabled, fxPress, fxRelease]);
};

export const FxGrid = ({ compact = false }) => {
  const { fxVol, setFxVol, fxMaster, setFxMaster } = usePlayer();
  const { counts } = useLiveStats();
  const active = useEngineActive();
  const [fx, setFx] = useState([]);
  const { t } = useT();

  useEffect(() => {
    api.get("/fx").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setFx(list);
      list.forEach((f) => loadBuffer(f.id, mediaUrl(f.file_id)).catch(() => {}));
    }).catch(() => {});
  }, []);

  useKeys(fx, !compact);

  return (
    <div>
      <FxModeBar compact={compact} />
      <MixFxRow compact={compact} active={active} />
      <div className="flex items-center gap-4 mb-6 max-w-sm">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4] whitespace-nowrap">{t("fx.master")}</span>
        <Slider data-testid={compact ? "fx-master-volume-compact" : "fx-master-volume"} value={[Math.round(fxMaster * 100)]} max={100} step={1} onValueChange={(v) => setFxMaster(v[0] / 100)} />
        <span className="font-mono text-xs w-10 text-right">{Math.round(fxMaster * 100)}</span>
      </div>
      {!fx.length ? <p className="font-mono text-sm text-[#A8B3C4]">{t("fx.empty")}</p> : (
        <div className={`grid gap-4 ${compact ? "grid-cols-4" : "grid-cols-2 sm:grid-cols-4"}`}>
          {fx.map((f, i) => (
            <Pad key={f.id} compact={compact} fx={f} idx={i} on={active.has(f.id)} hits={counts.fx[f.id]?.hits ?? f.hits ?? 0}
              vol={fxVol[f.id] ?? 0.8} setVol={(v) => setFxVol((s) => ({ ...s, [f.id]: v }))} />
          ))}
        </div>
      )}
    </div>
  );
};
